// src/pages/admin/healthcare/appointmentTypeComponents/PractitionerWorkflowsTab.tsx

import React, { useState } from "react";
import { Space, Typography, Tag, Switch, Button, Modal, Select, Tooltip } from "antd";
import { ContrastTag } from "../../../../../components/shared";
import InlineLoading from "../../../../../components/shared/InlineLoading";
import EmptyState from "../../../../../components/shared/EmptyState";
import { BranchesOutlined, EditOutlined, DeleteOutlined, PlusOutlined, RobotOutlined } from "@ant-design/icons";
import { savePractitionerWorkflowOverride, updatePractitionerWorkflowOverride, deletePractitionerWorkflowOverride, PractitionerWorkflowOverride } from "../../../../../api/healthcare/PractitionerWorkflowApi";
import { useNotifications } from "../../../../../context/NotificationContext";
import type { UserUuid } from "../../../../../types/identity";

const { Text } = Typography;
const { confirm } = Modal;

interface PractitionerWorkflowsTabProps {
	appointmentType: any;
	practitioners: any[];
	allWorkflows: any[];
	overrides: Record<UserUuid, PractitionerWorkflowOverride[]>;
	onRefresh: () => void;
	loading: boolean;
}

const PractitionerWorkflowsTab: React.FC<PractitionerWorkflowsTabProps> = ({ appointmentType, practitioners, allWorkflows, overrides, onRefresh, loading }) => {
	const [editModalVisible, setEditModalVisible] = useState(false);
	const [selectedPractitioner, setSelectedPractitioner] = useState<any>(null);
	const [selectedWorkflowId, setSelectedWorkflowId] = useState<number | null>(null);
	const [autoGenerate, setAutoGenerate] = useState(false);
	const [saving, setSaving] = useState(false);
	const { showSuccess, showError } = useNotifications();

	const handleOpenEditModal = (practitioner: any) => {
		setSelectedPractitioner(practitioner);

		// Check if practitioner has override
		const override = overrides[practitioner.id]?.[0];
		if (override) {
			setSelectedWorkflowId(override.workflow_template_id);
			setAutoGenerate(override.auto_generate);
		} else {
			setSelectedWorkflowId(null);
			setAutoGenerate(false);
		}

		setEditModalVisible(true);
	};

	const handleSaveOverride = async () => {
		if (!selectedPractitioner || !selectedWorkflowId) {
			showError("Please select a workflow");
			return;
		}

		setSaving(true);
		try {
			const existingOverride = overrides[selectedPractitioner.id]?.[0];

			if (existingOverride) {
				await updatePractitionerWorkflowOverride(existingOverride.id, {
					workflow_template_id: selectedWorkflowId,
					is_active: true,
					auto_generate: autoGenerate,
				});
			} else {
				await savePractitionerWorkflowOverride({
					practitioner_id: selectedPractitioner.id,
					appointment_type_id: appointmentType.id,
					workflow_template_id: selectedWorkflowId,
					is_active: true,
					auto_generate: autoGenerate,
				});
			}

			showSuccess("Practitioner workflow saved successfully");
			setEditModalVisible(false);
			onRefresh();
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to save workflow");
		} finally {
			setSaving(false);
		}
	};

	const handleRemoveOverride = async (practitionerId: UserUuid) => {
		const override = overrides[practitionerId]?.[0];
		if (!override) return;

		confirm({
			title: "Remove Workflow Override?",
			content: "This practitioner will use the appointment type's default workflow.",
			okText: "Remove",
			okType: "danger",
			cancelText: "Cancel",
			onOk: async () => {
				try {
					await deletePractitionerWorkflowOverride(override.id);
					showSuccess("Override removed successfully");
					onRefresh();
				} catch (error: any) {
					showError(error.response?.data?.message || "Failed to remove override");
				}
			},
		});
	};

	const handleToggleActive = async (practitionerId: UserUuid, isActive: boolean) => {
		const override = overrides[practitionerId]?.[0];
		if (!override) return;

		try {
			await updatePractitionerWorkflowOverride(override.id, {
				is_active: isActive,
			});
			showSuccess("Override updated successfully");
			onRefresh();
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to update override");
		}
	};

	const handleToggleAutoGenerate = async (practitionerId: UserUuid, autoGen: boolean) => {
		const override = overrides[practitionerId]?.[0];
		if (!override) return;

		try {
			await updatePractitionerWorkflowOverride(override.id, {
				auto_generate: autoGen,
			});
			showSuccess("Auto-generate updated successfully");
			onRefresh();
		} catch (error: any) {
			showError(error.response?.data?.message || "Failed to update auto-generate");
		}
	};

	if (loading) {
		return <InlineLoading minHeight={200} message="Loading overrides…" />;
	}

	if (!practitioners || practitioners.length === 0) {
		return <EmptyState title="No practitioners assigned" description="Assign practitioners to this appointment type first" />;
	}

	// Get default workflow info
	const defaultWorkflow = appointmentType.workflow_assignments?.[0]?.workflow_template;

	return (
		<div>
			<div style={{ marginBottom: 16 }}>
				<Text strong style={{ fontSize: 14 }}>
					Practitioner-Specific Workflows
				</Text>
				<div style={{ marginTop: 4 }}>
					<Text type="secondary" style={{ fontSize: 12 }}>
						Configure custom workflows for individual practitioners. These override the default workflow for this appointment type.
					</Text>
				</div>
				{defaultWorkflow && (
					<div style={{ marginTop: 8, padding: 8,  borderRadius: 4 }}>
						<Text type="secondary" style={{ fontSize: 11 }}>
							Default workflow: <Text strong>{defaultWorkflow.name}</Text>
						</Text>
					</div>
				)}
			</div>

			<Space direction="vertical" style={{ width: "100%" }} size="middle">
				{practitioners.map((practitioner) => {
					const override = overrides[practitioner.id]?.[0];
					const hasOverride = !!override;

					return (
						<div key={practitioner.id} style={{ border: "1px solid var(--mg-border)", borderRadius: 8, padding: "12px 16px", background: "var(--mg-bg-elevated)" }}>
							<div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
								<div style={{ flex: 1 }}>
									<div style={{ marginBottom: 8 }}>
										<Text strong>{`${practitioner.first_name} ${practitioner.last_name}`}</Text>
										{practitioner.team_member_info?.specialties?.[0] && (
											<ContrastTag background="var(--mg-info)" style={{ marginLeft: 8, fontSize: 10 }}>
												{practitioner.team_member_info.specialties[0]}
											</ContrastTag>
										)}
									</div>

									{hasOverride ? (
										<>
											<div style={{ marginBottom: 8 }}>
												<Space>
													<BranchesOutlined style={{ fontSize: 12 }} />
													<Text style={{ fontSize: 12 }}>{override.workflow_template?.name || "Custom Workflow"}</Text>
													<ContrastTag background={override.is_active ? "var(--mg-success)" : undefined} style={{ fontSize: 10 }}>
														{override.is_active ? "Active" : "Inactive"}
													</ContrastTag>
													{override.auto_generate && (
														<ContrastTag background="var(--mg-info)" icon={<RobotOutlined />} style={{ fontSize: 10 }}>
															Auto-Generate
														</ContrastTag>
													)}
												</Space>
											</div>
											{override.workflow_template?.description && (
												<Text type="secondary" style={{ fontSize: 11, display: "block" }}>
													{override.workflow_template.description}
												</Text>
											)}
										</>
									) : (
										<Text type="secondary" style={{ fontSize: 12 }}>
											Using default workflow
										</Text>
									)}
								</div>

								<Space direction="vertical" size="small" align="end">
									{hasOverride ? (
										<>
											<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
												<Text style={{ fontSize: 11 }}>Active:</Text>
												<Switch size="small" checked={override.is_active} onChange={(checked) => handleToggleActive(practitioner.id, checked)} />
											</div>
											<div style={{ display: "flex", alignItems: "center", gap: 8 }}>
												<Tooltip title="Automatically generate consultation guidance when appointment is created">
													<Text style={{ fontSize: 11 }}>Auto-gen:</Text>
												</Tooltip>
												<Switch size="small" checked={override.auto_generate} onChange={(checked) => handleToggleAutoGenerate(practitioner.id, checked)} disabled={!override.is_active} />
											</div>
											<Space size="small">
												<Button size="small" icon={<EditOutlined />} onClick={() => handleOpenEditModal(practitioner)}>
													Edit
												</Button>
												<Button size="small" danger icon={<DeleteOutlined />} onClick={() => handleRemoveOverride(practitioner.id)}>
													Remove
												</Button>
											</Space>
										</>
									) : (
										<Button size="small" icon={<PlusOutlined />} onClick={() => handleOpenEditModal(practitioner)}>
											Add Custom Workflow
										</Button>
									)}
								</Space>
							</div>
						</div>
					);
				})}
			</Space>

			{/* Edit Modal */}
			<Modal
				title={`Set Workflow for ${selectedPractitioner?.first_name} ${selectedPractitioner?.last_name}`}
				open={editModalVisible}
				onCancel={() => setEditModalVisible(false)}
				onOk={handleSaveOverride}
				okText="Save"
				okButtonProps={{ disabled: !selectedWorkflowId, loading: saving }}
				width={600}
			>
				<Space direction="vertical" style={{ width: "100%" }} size="middle">
					<div>
						<Text strong>Select Workflow</Text>
						<Text type="secondary" style={{ fontSize: 12, display: "block", marginTop: 4 }}>
							This workflow will override the appointment type's default workflow for this practitioner.
						</Text>
					</div>

					<Select
						style={{ width: "100%" }}
						placeholder="Choose a workflow"
						value={selectedWorkflowId}
						onChange={setSelectedWorkflowId}
						options={allWorkflows
							.filter((w) => w.is_active)
							.map((w) => ({
								label: (
									<Space>
										<span>{w.name}</span>
										<ContrastTag background="var(--mg-info)" style={{ fontSize: 10 }}>
											v{w.version}
										</ContrastTag>
									</Space>
								),
								value: w.id,
							}))}
					/>

					{selectedWorkflowId && (
						<div style={{ marginTop: 16 }}>
							<Space align="start">
								<Switch checked={autoGenerate} onChange={setAutoGenerate} />
								<div>
									<Text>Auto-generate on appointment creation</Text>
									<br />
									<Text type="secondary" style={{ fontSize: 11 }}>
										Automatically generate consultation guidance when an appointment is created
									</Text>
								</div>
							</Space>
						</div>
					)}
				</Space>
			</Modal>
		</div>
	);
};

export default PractitionerWorkflowsTab;
